// Built once during loading. Repeated solid pieces share one instanced draw per material.
export const addTollocanInfrastructure = (THREE, group, resources, {
  roadSurfaceY, centerZ, loopLength, concreteTexture,
}) => {
  const geometry = new THREE.BoxGeometry(1, 1, 1);
  const batches = new Map();
  const palette = { asphalt: 0x343a3b, concrete: 0x92958a, steel: 0xb6bbb5, wood: 0x665448, white: 0xd9d8bd };
  const transform = new THREE.Object3D();
  resources.push(geometry);
  const box = (kind, x, y, z, width, height, depth) => {
    if (!batches.has(kind)) batches.set(kind, []);
    transform.position.set(x, roadSurfaceY + y, z);
    transform.scale.set(width, height, depth);
    transform.updateMatrix();
    batches.get(kind).push(transform.matrix.clone());
  };
  const startZ = centerZ - loopLength / 2;
  [-7.8, 7.8].forEach((x) => box('white', x, 0.035, centerZ, 0.14, 0.015, loopLength));

  // Lateral carriageways beyond the planted medians, with two lanes each.
  [-24, 27].forEach((x) => {
    box('asphalt', x, 0, centerZ, 10, 0.06, loopLength);
    [-5.1, 5.1].forEach((edge) => {
      box('concrete', x + edge, 0.12, centerZ, 0.28, 0.24, loopLength);
      box('white', x + edge * 0.9, 0.045, centerZ, 0.12, 0.015, loopLength);
    });
    for (let z = startZ + 4; z < startZ + loopLength; z += 8) {
      box('white', x, 0.045, z, 0.14, 0.015, 3.4);
    }
  });
  // Raised sleepers and bright continuous steel heads make the railway legible at grazing angles.
  box('concrete', 13.5, 0.015, centerZ, 5.4, 0.08, loopLength);
  for (let z = startZ + 0.7; z < startZ + loopLength; z += 1.4) {
    box('wood', 13.5, 0.13, z, 3.4, 0.18, 0.32);
  }
  [12.55, 14.45].forEach((x) => box('steel', x, 0.29, centerZ, 0.16, 0.2, loopLength));

  // One pedestrian bridge per full scenery loop, with open railings and real stairs.
  const bridgeZ = startZ + loopLength * 0.28;
  box('concrete', 1.5, 8.2, bridgeZ, 68, 0.55, 2.6);
  [-32, -9, 17.5, 35].forEach((x) => box('concrete', x, 4, bridgeZ, 0.75, 8, 1));
  [-1.2, 1.2].forEach((offset) => {
    [8.7, 9.5].forEach((y) => box('steel', 1.5, y, bridgeZ + offset, 68, 0.1, 0.1));
    for (let x = -32; x <= 35; x += 1.7) box('steel', x, 8.95, bridgeZ + offset, 0.075, 1.3, 0.075);
  });
  [-32, 35].forEach((x) => {
    for (let step = 0; step < 32; step += 1) {
      const y = 8.2 - step * 0.25;
      const z = bridgeZ + 1.5 + step * 0.45;
      box('concrete', x, y, z, 2.3, 0.25, 0.48);
      [-1.08, 1.08].forEach((side) => {
        box('steel', x + side, y + 0.65, z, 0.07, 1.3, 0.07);
        box('steel', x + side, y + 1.25, z, 0.1, 0.1, 0.52);
      });
    }
  });

  // Direction boards are canvas typography, not image requests or per-frame text rendering.
  const sign = (x, z, title, destination, overhead = false) => {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 192;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#18594a';
    ctx.fillRect(0, 0, 512, 192);
    ctx.strokeStyle = '#e4e6d8';
    ctx.lineWidth = 5;
    ctx.strokeRect(8, 8, 496, 176);
    ctx.fillStyle = '#f3f2e3';
    ctx.textAlign = 'center';
    ctx.font = 'bold 35px sans-serif';
    ctx.fillText(title, 250, 66);
    ctx.font = '28px sans-serif';
    ctx.fillText(destination, 250, 114);
    ctx.font = 'bold 38px sans-serif';
    ctx.fillText(overhead ? '↓    ↓' : '↗', 250, 163);
    const texture = new THREE.CanvasTexture(canvas);
    texture.encoding = THREE.sRGBEncoding;
    const signGeometry = new THREE.PlaneGeometry(7.6, 2.85);
    const material = new THREE.MeshBasicMaterial({ map: texture, side: THREE.DoubleSide });
    const mesh = new THREE.Mesh(signGeometry, material);
    const y = overhead ? 7.1 : 4.6;
    mesh.position.set(x, roadSurfaceY + y, z);
    group.add(mesh);
    resources.push(texture, signGeometry, material);
    if (overhead) {
      [-8.8, 8.8].forEach((post) => box('steel', post, 4.4, z - 0.15, 0.22, 8.8, 0.22));
      box('steel', 0, 8.6, z - 0.15, 17.8, 0.22, 0.22);
    } else {
      [-2.8, 2.8].forEach((post) => box('steel', x + post, 2.2, z - 0.1, 0.12, 4.4, 0.12));
    }
  };
  sign(0, startZ + loopLength * 0.68, 'PASEO TOLLOCAN', 'TOLUCA CENTRO', true);
  sign(-13, startZ + loopLength * 0.08, 'METEPEC', 'SALIDA LATERAL');
  sign(18, startZ + loopLength * 0.9, 'CD. DE MÉXICO', 'LERMA');

  batches.forEach((matrices, kind) => {
    const material = new THREE.MeshStandardMaterial({
      color: palette[kind],
      map: kind === 'concrete' ? concreteTexture : null,
      roughness: 0.85,
    });
    const mesh = new THREE.InstancedMesh(geometry, material, matrices.length);
    matrices.forEach((matrix, index) => mesh.setMatrixAt(index, matrix));
    mesh.instanceMatrix.needsUpdate = true;
    // Three r128 does not calculate bounds from instance transforms.
    mesh.frustumCulled = false;
    group.add(mesh);
    resources.push(material, mesh);
  });
};
